import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCRefLovProps, RCCRefSpreadsheetProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import Spreadsheet from "src/components/Spreadsheet";
import LovCoins from "src/components/Lov/Coins";
import DateTimePicker from "src/components/DateTimePicker";
import { standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import isValidDate from "src/utils/isValidDate";
import { ICoinConversion } from "./interface";

const CoinConversion: React.FC<ICoinConversion> = ({ 
	errors,
	setValue,
	control,
	coinId,
	updateValues
}) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast} = RCCHookToast(); 
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const { confirm } = useConfirmModal();

	const lovCoin1 = useRef<RCCRefLovProps>(null);
	const lovCoin2 = useRef<RCCRefLovProps>(null);

	const [changedSpreadSheet, setChangedSpreadSheet] = useState<boolean>(false);
	const [coin1Id, setCoin1Id] = useState<number | undefined>(coinId);
	const [coin2Id, setCoin2Id] = useState<number | undefined>();
	const [baseYear, setBaseYear] = useState<string>(new Date().getFullYear().toString());

	const [valuesMonthlyData, setValuesMonthlyData] = useState<{ year: string; data: string[][] }[]>([]);
	const [spreadsheetData, setSpreadsheetData] = useState<string[][]>([]);

	const changeCheck = async(callback: Function) => {
		if(changedSpreadSheet){
			confirm(
				translate("Salvar alterações"), 
				`${translate("Deseja realmente salvar as alterações feitas")}?`,
				async () => {
					await RCCServicesApi.postRequest({
						method: "Coins/UpdateConversion",
						body: {
							coin1: coin1Id,
							coin2: coin2Id,
							currentLanguage,
							motba002001: valuesMonthlyData
						}
					});
					addToast({
						color: "green",
						description: translate("Conversões de moeda alteradas com sucesso"),
						title: translate("Sucesso")
					});
		
					callback();
				},
				(err) => {
					standardErrorMessages(err, translate("Erro ao alterar conversões de moeda"), addToast);
				},
				false,
				() => callback()
			);
		}else{
			callback();
		}
	};

	const requestConversion = async() => {
		try {
			setSpreadsheetData([]);
		
			const {data} = await RCCServicesApi.getRequest({
				method: "Coins/ListConversion",
				urlParameters: {
					coin1: coin1Id,
					coin2: coin2Id,
					currentLanguage
				}
			});

			const dados = data as { year: string; data: string[][] }[];
			setValuesMonthlyData(dados);
			updateSpreadSheet(dados);

		} catch (error) {
			standardErrorMessages(error,translate("Erro ao buscar conversões de moeda"), addToast);
		}
	};

	const handleExchangeCoins = () => {
		const coin1 = lovCoin1.current?.getValue() as Array<{value:string,label:string}>;
		const coin2 = lovCoin2.current?.getValue() as Array<{value:string,label:string}>;

		if(coin1.length > 0 && coin2.length > 0){
			changeCheck(() => {
				const coin1Value = coin1[0].value;
				const coin2Value = coin2[0].value;

				lovCoin1.current?.setValue(coin2 || "");
				lovCoin2.current?.setValue(coin1 || "");

				setValue("coin1", String(coin2Value));
				setValue("coin2", String(coin1Value));

				setCoin1Id(Number(coin2Value));
				setCoin2Id(Number(coin1Value));
			});
		}else{
			addToast({
				color: "yellow",
				description: translate("Informe Moeda de Origem e Moeda de Destino"),
				title: translate("Atenção")
			});
		}		
	};

	useEffect(() => {
		if(coin1Id && coin2Id){
			if(Number(coin1Id) !== Number(coin2Id)){
				requestConversion();
			}else if(!changedSpreadSheet){
				setSpreadsheetData([]);
				addToast({
					color: "yellow",
					description: translate("Moeda de Origem e Moeda de Destino devem ser diferentes"),
					title: translate("Atenção")
				});
			}
		}
	}, [coin1Id, coin2Id]);
	
	useEffect(() => {
		if(coin1Id && coin2Id){
			updateSpreadSheet(valuesMonthlyData);
		}
	}, [baseYear]);

	useEffect(() => {
		if(spreadsheetData.length === 0){
			setChangedSpreadSheet(false);
		}
	}, [spreadsheetData]);

	const updateSpreadSheet = (data: any) => {
		let valuesYear = data.find((x: any) => x.year === baseYear);

		if(!valuesYear){
			valuesYear = {
				year: baseYear,
				data: [
					["0", `01/${baseYear}`, "0"],
					["0", `02/${baseYear}`, "0"],
					["0", `03/${baseYear}`, "0"],
					["0", `04/${baseYear}`, "0"],
					["0", `05/${baseYear}`, "0"],
					["0", `06/${baseYear}`, "0"],
					["0", `07/${baseYear}`, "0"],
					["0", `08/${baseYear}`, "0"],
					["0", `09/${baseYear}`, "0"],
					["0", `10/${baseYear}`, "0"],
					["0", `11/${baseYear}`, "0"],
					["0", `12/${baseYear}`, "0"]
				]
			};

			valuesMonthlyData.push(valuesYear);
		}		

		setSpreadsheetData(valuesYear?.data || []);
	};

	const onSaveSpreadsheet = () => {
		if(!spreadsheetRef.current)
			return;	
			
		let index = valuesMonthlyData.findIndex((value) => value.year === baseYear);

		if(index === -1){
			valuesMonthlyData.push({
				year: baseYear,
				data: {} as string[][]
			});
			index = valuesMonthlyData.findIndex((value) => value.year === baseYear);
		}
			
		const newValuesMonthlyData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];

		setValuesMonthlyData((prevState) => {
			const updatePrevState = prevState;
			updatePrevState[index].data = newValuesMonthlyData;
			return updatePrevState;
		});	

		setChangedSpreadSheet(true);
		updateValues(valuesMonthlyData);
	};

	return(
		<>
			<RCCGrid container item spacing={2}>
				<RCCGrid container item>
					<RCCGrid container item sm={6}> 
						<LovCoins
							ref={lovCoin1}
							name="coin1"
							error={errors.coin1}
							label={translate("Moeda origem")}
							control={control}
							defaultValue={coinId?.toString()}
							onChangeCapture={(e: any) => {
								changeCheck(() => {
									const newValue = e ? e.value : undefined;
									setCoin1Id(newValue);
								});								
							}}
							required
						/>
						
					</RCCGrid>
					<RCCGrid styleInline="align-self: center; margin: 13px 0px 0px 7px;">
						<RCCTooltip
							text={translate("Trocar Moedas")}
							color="blue"
						>
							<RCCButton
								icon={{name: "exchange", size: 11}}
								outlined
								color="blue"
								size="xs"
								layout="default"
								onClick={handleExchangeCoins}
							/>
						</RCCTooltip>
					</RCCGrid>
					<RCCGrid container item sm={5} 
						wrap="nowrap" 
						alignItems="baseline" 
						styleInline={`&.MuiGrid-root {
									@media(min-width: 600px){padding-left: 8px}
								}`
						}> 
						<LovCoins
							ref={lovCoin2}
							name="coin2"
							error={errors.coin2}
							label={translate("Moeda destino")}
							control={control}
							onChangeCapture={(e: any) => {
								changeCheck(() => {
									const newValue = e ? e.value : undefined;
									setCoin2Id(newValue);
								});
							}}
							required
						/>
					</RCCGrid>
				</RCCGrid>
				<RCCGrid container item xs={5} sm={4} lg={3}>
					<DateTimePicker
						type="datePicker" 
						control={control}
						name="baseYear"
						label={translate("Ano base")}
						defaultValue={new Date(new Date().getFullYear() , 0, 1)}
						onChange={(date: any) => {
							if(isValidDate(date))
								setBaseYear(date.getFullYear().toString());
						}}
						props={{
							datePicker: {
								format: "yyyy",
								views: ["year"]
							}
						}}
						required
					/>
				</RCCGrid>
				<RCCGrid container item spacing={1}>
					{
						spreadsheetData.length === 0 ?
							<>
								<RCCAlert
									color="grey"
									title={translate("Nenhuma conversão encontrada")}
									description={translate("Selecione moeda de origem e de destino")}
								/>
							</>

							:

							<>
								<RCCGrid container item>
									<Spreadsheet 
										ref={spreadsheetRef}
										data={spreadsheetData}
										columns={ 
											[
												{ type: "hidden", title: "", width: 0 },
												{ type: "text", title: " ", width: 80, readOnly: true, wordWrap: true },
												{ type: "numeric", title: translate("Conversão"), readOnly: false, width: 130,  decimal: "," }
											]
										}
										format={{
											columns: [2]
										}}
										decimalPlaces={5}
										onChange={onSaveSpreadsheet}
									/>
								</RCCGrid>
							</>
					}
				</RCCGrid>
			</RCCGrid>
		</>
		
	);
};
export default CoinConversion;
